import { ApiError } from '@clockwise/shared';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImportSheet } from '@/components/custom/import-sheet';
import { customerBudgetsApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';

interface CustomerBudgetImportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CustomerBudgetImportSheet({ open, onOpenChange }: CustomerBudgetImportSheetProps) {
  const queryClient = useQueryClient();

  return (
    <ImportSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Import budgets"
      description="Upload an .xlsx or .csv. Each row is a complete budget — an optional Engagement (by name), one column per service role. A row matching the stored budget is skipped; any other row creates a new budget."
      onImport={async (file, { idempotencyKey }) => {
        const res = await customerBudgetsApi.importFile(file, { idempotencyKey });
        return {
          totalRows: res.data.totalRows,
          created: res.data.created,
          updated: res.data.updated,
          // Budgets are versioned rather than edited, so a re-import of an
          // unedited export is all-unchanged — reported honestly instead of
          // folded into `created`.
          unchanged: res.data.unchanged,
          failed: res.data.failed,
          rows: res.data.rows.map((r) => ({
            rowNumber: r.rowNumber,
            outcome: r.outcome,
            errors: r.errors,
          })),
        };
      }}
      onSuccess={() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.customerBudgets.all });
      }}
      onDownloadTemplate={async () => {
        try {
          const { blob, filename } = await customerBudgetsApi.exportTemplate();
          triggerDownload(blob, filename ?? 'customer-budgets-template.xlsx');
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : 'Template download failed';
          toast.error(msg);
        }
      }}
    />
  );
}
